/**
 * «Команди диспетчера» (/sko) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений legacy-рендер у page.tsx.
 *
 * Що виправлено проти старої версії:
 *  · власна палітра (#0066FF / #10B981 / #F59E0B / #1A1A1A) і 88 рядків
 *    Tailwind-класів (bg-blue-50, text-red-700, bg-green-100 …) — на токени;
 *    ті класи були БЕЗ dark:-варіантів, тож у темній темі банери й статус-пілюлі
 *    лишались світлими зі світлим текстом;
 *  · «браузерна» смуга вкладок → сегментована пілюля в топбарі (useSkoUi);
 *  · рамковані бокси → скляні .tile, один рівень;
 *  · font-mono (14 місць) → font-display, як вимагає §0;
 *  · червоний прибрано зі статусу pending: очікування дії — не збій (§3).
 *
 * Логіку, запити й обробники не змінено.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, API_BASE_URL } from '@/services/api';
import { useSkoUi } from '@/hooks/useSkoUi';
import {
  AlertCircle, Check, CheckCheck, ChevronRight, ExternalLink, Info, Lightbulb,
  Play, Plus, X, type LucideIcon,
} from 'lucide-react';

// ─────────────────────────── типи (дзеркало page.tsx) ───────────────────────────
interface PrsSubmission {
  id: string; assetId: string; assetName: string; scheduleDate: string;
  revisionNumber: number; mrid: string; senderXCode: string; registeredWCode: string;
  processType: string; targetEndpoint: string; pointsCount: number;
  totalEnergyMwh: number | null; status: string; submittedAt: string | null;
  sourceModelVersion: string; sourceForecastRunAt: string | null;
  xmlBytes: number; createdAt: string;
}
interface CurtailmentEvent {
  id: string; assetId: string; assetName: string; installedCapacityAcMw: number | null;
  eventKind: string; source: string | null; startedAtLocal: string; endedAtLocal: string | null;
  curtailmentPct: number | null; curtailmentPmaxKw: number | null; reason: string | null;
  dispatcherName: string | null; dispatcherOrg: string | null; notes: string | null;
  actualCompliancePct: number | null; relatedGridLoss: boolean; createdAt: string; updatedAt: string;
}
interface AssetOption {
  id: string; assetName: string; xCode: string; wCode: string; installedCapacityAcMw: number | null;
}
interface SkoCmd {
  id: string; commandType: string; severity: string; assetId: string | null; assetName: string | null;
  receivedVia: string; dispatcherName: string | null; receivedAt: string;
  effectiveFrom: string | null; effectiveUntil: string | null; ackDeadline: string | null;
  curtailmentPct: number | null; targetKw: number | null; reason: string | null;
  status: string; actualCompliancePct: number | null;
}

const fmtDate = (d: string | null): string => (!d ? '—' : d.length >= 10 ? d.slice(0, 10) : d);
const fmtDateTime = (d: string | null): string => (!d ? '—' : d.replace('T', ' ').slice(0, 16));
const fmtNum = (v: any, digits = 0): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : n.toLocaleString('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

// ─────────────────────────── спільні примітиви ───────────────────────────
const th: any = {
  textAlign: 'left', padding: '7px 10px', fontSize: 10.5, fontWeight: 600,
  color: 'var(--text-muted)', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)',
  whiteSpace: 'nowrap',
};
const td: any = {
  textAlign: 'left', padding: '7px 10px', fontSize: 12.5,
  color: 'var(--text)', borderBottom: '1px solid var(--line)',
};

/** Контрол за стандартом §6: 36 / radius 10. */
const ctrl: any = {
  height: 36, borderRadius: 10, background: 'var(--surface-2)',
  border: '1px solid var(--line)', color: 'var(--text)', padding: '0 10px', fontSize: 12.5,
};

function Note({ tone, Icon, children }: { tone: 'info' | 'warn' | 'err'; Icon: LucideIcon; children: any }) {
  const c = tone === 'err' ? 'var(--err)' : tone === 'warn' ? 'var(--warn)' : 'var(--text-muted)';
  return (
    <div className="text-[12px] flex items-start gap-2 py-2 pl-3" style={{ borderLeft: `3px solid ${c}`, color: c }}>
      <Icon size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Статус-пілюля: колір лише через токени, червоний — тільки справжній збій. */
function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span className="text-[10px] px-1.5 py-0.5 whitespace-nowrap"
      style={{ borderRadius: 6, color, border: `1px solid ${color}`,
               background: `color-mix(in srgb, ${color} 10%, transparent)` }}>
      {label}
    </span>
  );
}

/** Дрібна кнопка-дія в рядку таблиці — пілюля 28/8. */
function RowBtn({ label, Icon, onClick, disabled }: { label: string; Icon: LucideIcon; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="text-[11px] px-2 inline-flex items-center gap-1 whitespace-nowrap"
      style={{ height: 28, borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--line)',
               color: 'var(--text)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
      <Icon size={12} strokeWidth={2} />{label}
    </button>
  );
}

function TileHead({ title, right }: { title: string; right?: any }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
      <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{title}</h3>
      {right}
    </div>
  );
}

const empty = (cols: number, text: string) => (
  <tr><td colSpan={cols} className="py-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{text}</td></tr>
);

// ─────────────────────────── PRS ───────────────────────────
function PrsSectionRd({ t }: { t: (k: string) => string }) {
  const [items, setItems] = useState<PrsSubmission[]>([]);
  const [selected, setSelected] = useState<PrsSubmission | null>(null);
  const [xmlPreview, setXmlPreview] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ submissions: PrsSubmission[] }>(`/sko/prs/list?days=${days}`)
      .then((r) => { if (!cancelled) setItems(r.submissions); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  const openPreview = async (item: PrsSubmission) => {
    setSelected(item);
    setXmlPreview(null);
    try {
      const r = await api.get<{ xmlPayload: string }>(`/sko/prs/${item.id}`);
      setXmlPreview(r.xmlPayload);
    } catch (e: any) {
      setXmlPreview(`<error: ${e?.message ?? 'failed'}>`);
    }
  };

  // Догма: відхилена подача — реальний збій обміну, решта станів нейтральні.
  const prsColor = (s: string) =>
    s === 'accepted' ? 'var(--ok)' : s === 'rejected' ? 'var(--err)'
      : s === 'submitted' ? 'var(--series-3)' : s === 'dry_run' ? 'var(--text-muted)' : 'var(--warn)';

  return (
    <div className="flex flex-col gap-3">
      <div className="tile p-4 sm:p-5">
        <Note tone="info" Icon={Info}>
          <strong>{t('sko_prs_banner_title')}.</strong> {t('sko_prs_banner_text')}
        </Note>
      </div>

      <div className="tile p-4 sm:p-5">
        <TileHead title={`${t('sko_prs_table_title')} (${items.length})`}
          right={
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} style={ctrl} className="cursor-pointer outline-none">
              {[7, 14, 30, 90].map((d) => <option key={d} value={d}>{d} {t('ueex_page_days')}</option>)}
            </select>
          } />
        {error && <div className="mb-3"><Note tone="err" Icon={AlertCircle}>{error}</Note></div>}
        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>{t('sko_prs_col_date')}</th>
              <th style={th}>{t('sko_prs_col_asset')}</th>
              <th style={th}>mRID</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('sko_prs_col_rev')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('sko_prs_col_points')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('sko_prs_col_energy')}</th>
              <th style={th}>{t('sko_prs_col_status')}</th>
              <th style={{ ...th, textAlign: 'right' }}></th>
            </tr></thead>
            <tbody>
              {items.length === 0 && empty(8, t('sko_no_data'))}
              {items.map((s) => (
                <tr key={s.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(s.scheduleDate)}</td>
                  <td style={td}>{s.assetName}</td>
                  <td style={{ ...td, fontSize: 11, color: 'var(--text-muted)' }}>{s.mrid}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right' }}>{s.revisionNumber}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right' }}>{s.pointsCount}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtNum(s.totalEnergyMwh, 2)} МВт·год</td>
                  <td style={td}><Pill label={s.status} color={prsColor(s.status)} /></td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div className="inline-flex items-center gap-1.5">
                      <RowBtn label={t('sko_prs_preview')} Icon={ChevronRight} onClick={() => openPreview(s)} />
                      <a href={`${API_BASE_URL}/sko/prs/${s.id}/download.xml`} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] px-2 inline-flex items-center gap-1"
                        style={{ height: 28, borderRadius: 8, background: 'var(--surface-2)',
                                 border: '1px solid var(--line)', color: 'var(--text)' }}>XML</a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="tile p-4 sm:p-5">
          <TileHead title={`${t('sko_prs_preview_title')}: ${selected.mrid}`}
            right={<RowBtn label={t('sko_close')} Icon={X} onClick={() => { setSelected(null); setXmlPreview(null); }} />} />
          {/* Пари «підпис / значення» без вкладених боксів — §3. */}
          <div className="grid gap-x-4 gap-y-3 pb-3 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', borderBottom: '1px solid var(--line)' }}>
            {([
              [t('sko_prs_col_asset'), selected.assetName],
              [t('sko_prs_col_date'), fmtDate(selected.scheduleDate)],
              ['X-код', selected.senderXCode],
              ['W-код', selected.registeredWCode],
              ['processType', selected.processType],
              [t('sko_prs_col_energy'), `${fmtNum(selected.totalEnergyMwh, 2)} МВт·год`],
              [t('sko_prs_col_points'), `${selected.pointsCount}`],
              ['endpoint', selected.targetEndpoint],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>{k}</div>
                <div className="text-[12px] mt-0.5 truncate" title={v} style={{ color: 'var(--text)' }}>{v}</div>
              </div>
            ))}
          </div>
          <pre className="p-3 overflow-x-auto text-[11px] leading-tight"
            style={{ background: 'var(--surface-2)', borderRadius: 10, border: '1px solid var(--line)',
                     color: 'var(--text-muted)', maxHeight: 340, whiteSpace: 'pre' }}>
            {xmlPreview ?? t('sko_loading')}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Curtailment ───────────────────────────
function CurtailmentSectionRd({ t }: { t: (k: string) => string }) {
  const [events, setEvents] = useState<CurtailmentEvent[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    api.get<{ events: CurtailmentEvent[] }>('/sko/curtailment-events?days=90')
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e?.message ?? 'load failed'));
  };

  useEffect(() => {
    reload();
    api.get<{ assets: AssetOption[] }>('/sko/assets').then((r) => setAssets(r.assets)).catch(() => {});
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <div className="tile p-4 sm:p-5">
        <Note tone="warn" Icon={Lightbulb}>
          <strong>{t('sko_curt_banner_title')}.</strong> {t('sko_curt_banner_text')}
        </Note>
      </div>

      <div className="tile p-4 sm:p-5">
        <TileHead title={`${t('sko_curt_events_title')} (${events.length})`}
          right={
            <button type="button" onClick={() => setShowForm(!showForm)}
              className="text-xs px-3 inline-flex items-center gap-1.5 cursor-pointer"
              style={{ height: 36, borderRadius: 10,
                       background: showForm ? 'var(--surface-2)' : 'var(--series-3)',
                       border: `1px solid ${showForm ? 'var(--line)' : 'var(--series-3)'}`,
                       color: showForm ? 'var(--text)' : '#fff' }}>
              {showForm ? <X size={14} strokeWidth={2} /> : <Plus size={14} strokeWidth={2} />}
              {showForm ? t('sko_close') : t('sko_curt_add_event')}
            </button>
          } />
        {error && <div className="mb-3"><Note tone="err" Icon={AlertCircle}>{error}</Note></div>}
        {showForm && <EventFormRd assets={assets} t={t} onCreated={() => { reload(); setShowForm(false); }} />}

        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>{t('sko_curt_col_asset')}</th>
              <th style={th}>{t('sko_curt_col_start')}</th>
              <th style={th}>{t('sko_curt_col_end')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('sko_curt_col_pct')}</th>
              <th style={th}>{t('sko_curt_col_reason')}</th>
              <th style={th}>{t('sko_curt_col_source')}</th>
              <th style={th}>{t('sko_curt_col_dispatcher')}</th>
            </tr></thead>
            <tbody>
              {events.length === 0 && empty(7, t('sko_no_data'))}
              {events.map((e) => (
                <tr key={e.id}>
                  <td style={td}>
                    {e.assetName}
                    {e.relatedGridLoss && <span className="ml-1.5"><Pill label="grid-loss" color="var(--warn)" /></span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(e.startedAtLocal)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {e.endedAtLocal ? fmtDateTime(e.endedAtLocal) : <Pill label={t('sko_curt_active')} color="var(--ok)" />}
                  </td>
                  <td className="font-display" style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {e.curtailmentPct !== null ? `${e.curtailmentPct}%`
                      : e.curtailmentPmaxKw !== null ? `${fmtNum(e.curtailmentPmaxKw, 0)} кВт` : '—'}
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>{e.reason || '—'}</td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>{e.source || '—'}</td>
                  <td style={{ ...td, fontSize: 12 }}>{e.dispatcherName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EventFormRd({ assets, t, onCreated }: { assets: AssetOption[]; t: (k: string) => string; onCreated: () => void }) {
  const [form, setForm] = useState({
    assetId: '', source: 'phone_dispatcher',
    startedAtLocal: new Date().toISOString().slice(0, 16),
    endedAtLocal: '', curtailmentPct: '', reason: '',
    dispatcherName: '', dispatcherOrg: 'Укренерго ОЕС', notes: '', relatedGridLoss: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.assetId) { setErr('Виберіть СЕС'); return; }
    setSaving(true); setErr(null);
    try {
      await api.post('/sko/curtailment-events', {
        ...form,
        curtailmentPct: form.curtailmentPct ? Number(form.curtailmentPct) : null,
        endedAtLocal: form.endedAtLocal || null,
      });
      onCreated();
    } catch (e: any) { setErr(e?.message ?? 'failed to save'); }
    finally { setSaving(false); }
  };

  const upd = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const lbl = 'text-[10.5px] block mb-1';
  const fld: any = { ...ctrl, width: '100%' };

  // Форма живе всередині плитки, тож власної рамки не має — лише лінія зверху (§3).
  return (
    <form onSubmit={submit} className="pt-3 mt-1 mb-4" style={{ borderTop: '1px solid var(--line)' }}>
      {err && <div className="mb-3"><Note tone="err" Icon={AlertCircle}>{err}</Note></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_asset')} *</label>
          <select value={form.assetId} onChange={(e) => upd('assetId', e.target.value)} style={fld} className="outline-none cursor-pointer" required>
            <option value="">— виберіть —</option>
            {assets.map((a) => <option key={a.id} value={a.id}>{a.assetName} ({a.installedCapacityAcMw} МВт)</option>)}
          </select>
        </div>
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_source')}</label>
          <select value={form.source} onChange={(e) => upd('source', e.target.value)} style={fld} className="outline-none cursor-pointer">
            <option value="phone_dispatcher">Телефон диспетчера</option>
            <option value="telegram">Telegram</option>
            <option value="email">Email</option>
            <option value="sko_api">СКО API</option>
            <option value="other">Інше</option>
          </select>
        </div>
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_dispatcher')} орг.</label>
          <input type="text" value={form.dispatcherOrg} onChange={(e) => upd('dispatcherOrg', e.target.value)} style={fld} className="outline-none" />
        </div>
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_start')} *</label>
          <input type="datetime-local" value={form.startedAtLocal} onChange={(e) => upd('startedAtLocal', e.target.value)} style={fld} className="outline-none" required />
        </div>
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_end')}</label>
          <input type="datetime-local" value={form.endedAtLocal} onChange={(e) => upd('endedAtLocal', e.target.value)} style={fld} className="outline-none"
            placeholder="лишити порожнім якщо ще триває" />
        </div>
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>Curtailment % (0-100)</label>
          <input type="number" min="0" max="100" step="1" value={form.curtailmentPct}
            onChange={(e) => upd('curtailmentPct', e.target.value)} style={fld} className="outline-none"
            placeholder="напр. 30 = до 30% від nameplate" />
        </div>
        <div className="md:col-span-2">
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_reason')}</label>
          <input type="text" value={form.reason} onChange={(e) => upd('reason', e.target.value)} style={fld} className="outline-none"
            placeholder="напр. Перевантаження ЛЕП, Аварія на ПС" />
        </div>
        <div>
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>{t('sko_curt_col_dispatcher')} ім&apos;я</label>
          <input type="text" value={form.dispatcherName} onChange={(e) => upd('dispatcherName', e.target.value)} style={fld} className="outline-none" />
        </div>
        <div className="md:col-span-3">
          <label className={lbl} style={{ color: 'var(--text-muted)' }}>Нотатки</label>
          <textarea value={form.notes} onChange={(e) => upd('notes', e.target.value)} rows={2}
            className="w-full outline-none px-2.5 py-2 text-[12.5px]"
            style={{ borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text)' }} />
        </div>
        <div className="md:col-span-3 flex items-center gap-2">
          <input type="checkbox" id="grid_loss_flag_rd" checked={form.relatedGridLoss}
            onChange={(e) => upd('relatedGridLoss', e.target.checked)} />
          <label htmlFor="grid_loss_flag_rd" className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
            Подія пов&apos;язана з grid-loss (anti-islanding), а не з командою диспетчера
          </label>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button type="submit" disabled={saving}
          className="text-xs px-4 inline-flex items-center gap-1.5"
          style={{ height: 36, borderRadius: 10, background: 'var(--series-3)',
                   border: '1px solid var(--series-3)', color: '#fff',
                   cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.55 : 1 }}>
          <Check size={14} strokeWidth={2} />{saving ? t('sko_saving') : t('sko_save')}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────── Про СКО ───────────────────────────
function AboutSectionRd({ t }: { t: (k: string) => string }) {
  const PHASES: [string, string, string][] = [
    ['ok', 'Phase A', 'PRS dry-run generator + curtailment events log (поточна)'],
    ['wait', 'Phase B', 'договір з ОСП, VPN, отримання production credentials'],
    ['wait', 'Phase C', 'telemetry IEC 60870-5-104 → ОСП'],
    ['wait', 'Phase D', 'прийом curtailment команд + автоматичне виконання'],
  ];
  return (
    <div className="tile p-4 sm:p-5">
      <h3 className="font-display text-base font-semibold mb-3" style={{ color: 'var(--text)' }}>{t('sko_about_title')}</h3>
      <div className="text-[13px] flex flex-col gap-3" style={{ color: 'var(--text-muted)' }}>
        <p>{t('sko_about_p1')}</p>
        <p>{t('sko_about_p2')}</p>
        <p>{t('sko_about_p3')}</p>
        <div className="pt-3" style={{ borderTop: '1px solid var(--line)' }}>
          <div className="text-[12px] font-semibold mb-2" style={{ color: 'var(--text)' }}>{t('sko_about_phase_status')}</div>
          <div className="flex flex-col gap-1.5">
            {PHASES.map(([kind, name, desc]) => (
              <div key={name} className="flex items-start gap-2 text-[12px]">
                {kind === 'ok'
                  ? <Check size={13} strokeWidth={2.2} style={{ color: 'var(--ok)', marginTop: 2, flexShrink: 0 }} />
                  : <ChevronRight size={13} strokeWidth={2.2} style={{ color: 'var(--text-dim)', marginTop: 2, flexShrink: 0 }} />}
                <span><b style={{ color: 'var(--text)' }}>{name}</b>: {desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Inbox команд ───────────────────────────
function InboxSectionRd({ t }: { t: (k: string) => string }) {
  const [items, setItems] = useState<SkoCmd[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [days, setDays] = useState(30);
  const [acking, setAcking] = useState<string | null>(null);

  const load = () => {
    const q = statusFilter === 'all' ? '' : `&status=${statusFilter}`;
    api.get<{ commands: SkoCmd[] }>(`/sko/commands?days=${days}${q}&limit=200`)
      .then((r) => { setItems(r.commands); setError(null); })
      .catch((e) => setError(e?.message ?? 'load failed'));
  };

  useEffect(() => { load(); const timer = setInterval(load, 60000); return () => clearInterval(timer); }, [statusFilter, days]);

  const act = async (id: string, fn: () => Promise<any>) => {
    setAcking(id);
    try { await fn(); load(); } finally { setAcking(null); }
  };

  /**
   * Догма: очікування дії — це УВАГА (бурштин), а не збій. Раніше pending був
   * червоним (bg-red-100), тобто читався як поломка системи.
   */
  const STATUS: Record<string, { color: string; label: string }> = {
    pending: { color: 'var(--warn)', label: 'PENDING' },
    acknowledged: { color: 'var(--text-muted)', label: 'ACKED' },
    executing: { color: 'var(--series-3)', label: 'EXECUTING' },
    completed: { color: 'var(--ok)', label: 'COMPLETED' },
    expired: { color: 'var(--text-dim)', label: 'EXPIRED' },
    rejected: { color: 'var(--text-dim)', label: 'REJECTED' },
    cancelled: { color: 'var(--text-dim)', label: 'CANCELLED' },
  };

  return (
    <div className="tile p-4 sm:p-5">
      <TileHead title={`${t('sko_inbox_full_title')} (${items.length})`}
        right={
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={ctrl} className="outline-none cursor-pointer">
              <option value="all">{t('sko_inbox_filter_all')}</option>
              {['pending', 'acknowledged', 'executing', 'completed', 'expired', 'rejected'].map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} style={ctrl} className="outline-none cursor-pointer">
              {[7, 30, 90].map((d) => <option key={d} value={d}>{d} {t('ueex_page_days')}</option>)}
            </select>
          </div>
        } />
      {error && <div className="mb-3"><Note tone="err" Icon={AlertCircle}>{error}</Note></div>}

      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {['Час', 'Тип', 'СЕС', 'Обмеження', 'Період', 'Причина', 'Диспетчер', 'Статус', 'Дії'].map((h, i) => (
              <th key={h} style={{ ...th, textAlign: i === 3 ? 'right' : 'left' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {items.length === 0 && empty(9, t('sko_no_data'))}
            {items.map((c) => {
              const st = STATUS[c.status] || STATUS.pending;
              return (
                <tr key={c.id}>
                  <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>{fmtDateTime(c.receivedAt)}</td>
                  <td style={{ ...td, fontSize: 12 }}>
                    {c.commandType}
                    {/* critical — реальна аварійна вказівка, єдине місце для --err */}
                    {c.severity === 'critical' && (
                      <AlertCircle size={12} strokeWidth={2.4} style={{ color: 'var(--err)', display: 'inline', marginLeft: 4, verticalAlign: 'text-top' }} />
                    )}
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>{c.assetName || '—'}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {c.curtailmentPct !== null ? `${c.curtailmentPct}%` : c.targetKw !== null ? `${c.targetKw} кВт` : '—'}
                  </td>
                  <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>
                    {c.effectiveFrom ? `${fmtDateTime(c.effectiveFrom).slice(11)}–${fmtDateTime(c.effectiveUntil).slice(11)}` : '—'}
                  </td>
                  <td className="truncate max-w-xs" style={{ ...td, fontSize: 12 }} title={c.reason || ''}>{c.reason || '—'}</td>
                  <td style={{ ...td, fontSize: 12 }}>{c.dispatcherName || '—'}</td>
                  <td style={td}>
                    <Pill label={st.label} color={st.color} />
                    {c.status === 'completed' && c.actualCompliancePct !== null && (
                      <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{c.actualCompliancePct}% comply</div>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {c.status === 'pending' && (
                      <RowBtn label="ACK" Icon={Check} disabled={acking === c.id}
                        onClick={() => act(c.id, () => api.post(`/sko/commands/${c.id}/ack`, { ackVia: 'web_ui' }))} />
                    )}
                    {c.status === 'acknowledged' && (
                      <RowBtn label="Виконання" Icon={Play} disabled={acking === c.id}
                        onClick={() => act(c.id, () => api.post(`/sko/commands/${c.id}/mark-executing`, { execMethod: 'manual_at_site' }))} />
                    )}
                    {c.status === 'executing' && (
                      <RowBtn label="Завершено" Icon={CheckCheck} disabled={acking === c.id}
                        onClick={() => act(c.id, () => api.post(`/sko/commands/${c.id}/complete`, {}))} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────── каркас сторінки ───────────────────────────
export default function SkoRedesign() {
  const { t } = useTranslation();
  const { section } = useSkoUi();

  return (
    <DashboardLayout breadcrumb={[t('nav_disp_commands')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>
          <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-2xl font-semibold leading-tight" style={{ color: 'var(--text)' }}>
                {t('sko_page_title')}
              </h1>
              <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{t('sko_page_subtitle')}</p>
            </div>
            <a href="https://ua.energy/uchasnikam-rinku/vidnovlyuvani-dzherela-energiyi/"
              target="_blank" rel="noopener noreferrer"
              className="text-[12px] inline-flex items-center gap-1.5 hover:underline whitespace-nowrap"
              style={{ color: 'var(--text-muted)' }}>
              {t('sko_external_link')}<ExternalLink size={12} strokeWidth={2} />
            </a>
          </div>

          {section === 'inbox' && <InboxSectionRd t={t} />}
          {section === 'prs' && <PrsSectionRd t={t} />}
          {section === 'curtailment' && <CurtailmentSectionRd t={t} />}
          {section === 'about' && <AboutSectionRd t={t} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
